import type { DirectiveBinding, ObjectDirective } from 'vue'
import { throttle } from 'lodash'

interface ResizeOptions {
  minWidth?: number
  maxWidth?: number
  rightSectionID?: string
  onResize?: (leftWidth: number, rightWidth?: number) => void
}

interface ResizeData {
  handle: HTMLElement
  onMouseDown: (e: MouseEvent) => void
  cleanup: () => void
}

// 新增：抽取初始化逻辑，避免在 updated 中调用 this.mounted（Vue3 无此上下文）
function setupResizeRight(el: HTMLElement, binding: DirectiveBinding<ResizeOptions>) {
  const options = {
    minWidth: binding.value?.minWidth ?? 50,
    maxWidth: binding.value?.maxWidth ?? 1000,
    rightSectionID: binding.value?.rightSectionID,
    onResize: binding.value?.onResize,
  }

  // 设置元素样式
  if (el.style.position === '' || el.style.position === 'static') {
    el.style.position = 'relative'
  }

  // 创建拖拽手柄
  const handle = document.createElement('div')
  handle.className = 'resize-handle-right'
  Object.assign(handle.style, {
    position: 'absolute',
    top: '0',
    right: '-6px',
    width: '12px',
    height: '100%',
    cursor: 'ew-resize',
    userSelect: 'none',
    background: 'transparent',
    zIndex: '10'
  })

  el.appendChild(handle)

  let startX = 0
  let startWidth = 0
  let rightStartWidth = 0
  let isResizing = false
  let rightSection: HTMLElement | null = null

  // 获取右侧元素
  if (options.rightSectionID) {
    rightSection = document.getElementById(options.rightSectionID)
    if (rightSection) {
      rightStartWidth = rightSection.offsetWidth
    }
  }

  // 节流处理的 mousemove 事件
  const throttledMouseMove = throttle((e: MouseEvent) => {
    if (!isResizing) return

    const diff = e.clientX - startX
    let newWidth = Math.max(options.minWidth, Math.min(options.maxWidth, startWidth + diff))
    
    // 计算右侧元素的新宽度
    let newRightWidth = rightStartWidth
    if (rightSection) {
      const totalWidth = startWidth + rightStartWidth
      newRightWidth = totalWidth - newWidth
      
      // 确保右侧元素也有最小宽度
      const rightMinWidth = 50 // 可以作为选项传入
      if (newRightWidth < rightMinWidth) {
        newRightWidth = rightMinWidth
        newWidth = totalWidth - rightMinWidth
      }
    }

    // 应用新宽度
    el.style.width = `${newWidth}px`
    if (rightSection) {
      rightSection.style.width = `${newRightWidth}px`
    }

    // 触发回调
    options.onResize?.(newWidth, rightSection ? newRightWidth : undefined)
  }, 16) // 约60fps

  const onMouseMove = (e: MouseEvent) => {
    e.preventDefault()
    throttledMouseMove(e)
  }

  const onMouseUp = () => {
    if (isResizing) {
      isResizing = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    startX = e.clientX
    startWidth = el.offsetWidth
    isResizing = true
    
    // 重新获取右侧元素的当前宽度
    if (rightSection) {
      rightStartWidth = rightSection.offsetWidth
    }
    
    // 设置全局样式防止选择和设置光标
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    
    document.addEventListener('mousemove', onMouseMove, { passive: false })
    document.addEventListener('mouseup', onMouseUp)
  }

  // 清理函数
  const cleanup = () => {
    if (handle.parentNode) {
      handle.removeEventListener('mousedown', onMouseDown)
      handle.parentNode.removeChild(handle)
    }
    // 确保移除可能残留的事件监听器
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    throttledMouseMove.cancel()
    
    // 恢复样式
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  handle.addEventListener('mousedown', onMouseDown)

  // 将数据存储到元素上以便后续清理
  ;(el as any).__resizeRightData__ = {
    handle,
    onMouseDown,
    cleanup
  } as ResizeData
}

export default {
  mounted(el: HTMLElement, binding: DirectiveBinding<ResizeOptions>) {
    setupResizeRight(el, binding)
  },

  unmounted(el: HTMLElement) {
    const data = (el as any).__resizeRightData__ as ResizeData | undefined
    if (data) {
      data.cleanup()
      delete (el as any).__resizeRightData__
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding<ResizeOptions>) {
    // 如果配置发生变化，重新初始化
    const data = (el as any).__resizeRightData__ as ResizeData | undefined
    if (data && binding.value !== binding.oldValue) {
      data.cleanup()
      // 重新挂载
      setupResizeRight(el, binding)
    }
  }
} as ObjectDirective<HTMLElement, ResizeOptions>
